<template>
    <div>
        <button
            class="btn btn-sm btn-primary"
            type="button"
            id="add-user"
            @click="showAddUserModal()"
        >
            <slot></slot>
        </button>

        <modal-component
            :id="'add-user-modal'"
            :visible="isVisible"
            title="Add or invite user"
            @close="resetModal()"
        >
            <template v-if="!newUserId">
                <div class="p-3">
                    <div class="pb-3">
                        <div class="alert alert-info">
                            If the user doesn't have a goLOTUS account yet, an e-mail will be sent
                            inviting to join the platform. Once this is done, the user will be added
                            to your organization.
                        </div>
                        <div class="form-group row">
                            <label for="email" class="col-6">E-mail:</label>
                            <div class="col-6">
                                <input
                                    id="email"
                                    name="email"
                                    type="text"
                                    class="form-control form-control-sm"
                                    v-model="payload.email"
                                />
                            </div>
                        </div>
                    </div>

                    <hr class="bar" />
                </div>

                <!-- ADD OR INVITE USER - STEP # 1-->
                <div v-show="!emailChecked" class="p-3">
                    <div class="d-flex justify-content-end w-100">
                        <div class="d-flex w-100 justify-content-end">
                            <button
                                class="btn btn-sm btn-primary text-capitalize font-weight-bold"
                                :disabled="!payload.email"
                                @click="checkEmail()"
                                dusk="next"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>

                <!-- ADD OR INVITE USER - STEP # 2-->
                <div v-show="emailChecked" class="p-3">
                    <!--ADD USER TO ORGANIZATION OR SEND INVITE-->
                    <div v-if="emailExists" class="alert alert-warning" dusk="email-exists">
                        <p>
                            There is already a goLOTUS user associated with that e-mail address. You
                            may invite that person to join your organization below.
                        </p>
                        <p>
                            Please note, you will not be able to edit their details until they
                            accept your invitation to join your organization.
                        </p>
                    </div>

                    <!--CREATE NEW USER-->
                    <div v-else="" class="pt-3 px-3" dusk="new-user">
                        <div class="form-group row">
                            <label for="first_name" class="col-6 mb-1">First Name:</label>
                            <input
                                id="first_name"
                                name="first_name"
                                type="text"
                                class="form-control form-control-sm col-6 mb-1"
                                v-model="payload.first_name"
                            />
                        </div>
                        <div class="form-group row">
                            <label for="last_name" class="col-6 mb-1">Last Name:</label>
                            <input
                                id="last_name"
                                name="last_name"
                                type="text"
                                class="form-control form-control-sm col-6 mb-1"
                                v-model="payload.last_name"
                            />
                        </div>
                    </div>

                    <div class="pb-3 px-3">
                        <div class="form-group row">
                            <label for="role_id" class="col-6">Role:</label>
                            <div class="col-6 px-0">
                                <select
                                    v-model="payload.role_id"
                                    id="role_id"
                                    class="form-control form-control-sm"
                                    required
                                >
                                    <option selected disabled :value="null"
                                        >Please choose one</option
                                    >
                                    <option
                                        v-for="r in roles.filter(r => r.id > 1)"
                                        :value="r.id"
                                        >{{ r.name }}</option
                                    >
                                </select>
                            </div>
                        </div>

                        <p>
                            Adding a user to the <b>{{ organization.name }}</b> account will
                            increase the subscription from <b>{{ subscription.quantity }}</b> seats
                            to <b>{{ subscription.quantity + 1 }}</b> seats.
                        </p>

                        <errors :errors="errors"></errors>
                    </div>

                    <invoice-preview
                        :organization="organization"
                        :subscription="subscription"
                        ref="previewAdd"
                    ></invoice-preview>

                    <div v-if="emailExists" class="d-flex w-100 justify-content-end">
                        <button
                            class="btn btn-sm btn-primary text-capitalize font-weight-bold"
                            id="invite-user-button"
                            @click="inviteUser()"
                        >
                            Invite user
                        </button>
                    </div>

                    <div v-else="" class="d-flex w-100 justify-content-end">
                        <button
                            class="btn btn-sm btn-primary text-capitalize font-weight-bold"
                            id="create-user-button"
                            @click="createUser()"
                        >
                            Create user
                        </button>
                    </div>
                </div>
            </template>

            <!-- SEND INVITE TO NEW USER - STEP # 3-->
            <template v-else="">
                <div class="p-3">
                    <p>
                        Would you like to send an invite e-mail to this user now? You can
                        alternatively send an invite at any time from the
                        <b>User Accounts</b> page in the <b>Control Center</b>.
                    </p>
                    <div class="w-100 d-flex justify-content-between align-items-center">
                        <button @click="resetModal" class="btn btn-outline-primary">
                            Send later
                        </button>
                        <button @click="sendInvite" class="btn btn-primary" id="send-invite-button">
                            Send now
                        </button>
                    </div>
                </div>
            </template>
        </modal-component>
    </div>
</template>

<script>
import ModalComponent from "../ui/ModalComponent";
import InvoicePreview from "../widgets/UpcomingInvoicePreview";
import { errors } from "../../plugins/errors";
import { helpers } from "../../plugins/helpers";

export default {
    name: "AddUserWidget",
    props: ["organization", "subscription", "roles"],
    components: {
        ModalComponent,
        InvoicePreview,
    },
    mixins: [errors],
    data() {
        return {
            isVisible: false,
            emailChecked: false,
            emailExists: false,
            payload: {
                first_name: null,
                last_name: null,
                email: null,
                password: null,
                password_confirmation: null,
                role_id: null,
            },
            newUserId: null,
        };
    },
    methods: {
        showAddUserModal() {
            this.$refs.previewAdd.getUpcomingInvoicePreview(1);
            this.isVisible = true;
        },

        resetModal() {
            this.setErrors();
            this.emailChecked = false;
            this.emailExists = false;
            this.isVisible = false;
            this.newUserId = null;
            this.payload = {
                first_name: null,
                last_name: null,
                email: null,
                role_id: null,
            };
        },

        checkEmail() {
            let email = `${this.payload.email}`;
            axios.get(`/api/users/exists?email=${email}`).then(response => {
                this.emailChecked = true;
                this.emailExists = response.data;
            });
        },

        validatePayload() {
            const payload = this.payload;
            if (!payload.first_name || !payload.last_name || !payload.email || !payload.role_id) {
                return null;
            }
            return payload;
        },

        inviteUser() {
            if (!this.payload.email || !this.payload.role_id) return;
            let payload = {
                email: this.payload.email,
                role_id: this.payload.role_id,
            };
            this.setErrors();
            helpers.toggleSpinner("invite-user-button");
            axios
                .post(`/api/organizations/${this.organization.id}/invites`, payload)
                .then(r => {
                    this.resetModal();
                    this.$emit("update-list");
                })
                .then(() => {
                    mixpanel.track("My Connections - Invite a Team Member", {
                        "New Subscription Quantity": this.subscription.quantity,
                    });
                    helpers.toggleSpinner("invite-user-button");
                    this.$swal("Done!", "The invite was sent successfully.", "success");
                })
                .catch(e => {
                    this.setErrors(_.get(e, "response.data.errors", {}));
                    console.error(e);
                    helpers.toggleSpinner("invite-user-button");
                });
        },

        createUser() {
            this.setErrors();
            helpers.toggleSpinner("create-user-button");
            const payload = this.validatePayload();
            if (!payload) {
                this.setErrors([["Please complete all fields."]]);
                helpers.toggleSpinner("create-user-button");
                return;
            }
            axios
                .post(`/api/organizations/${this.organization.id}/users`, payload)
                .then(response => {
                    this.subscription.quantity++;
                    helpers.toggleSpinner("create-user-button");
                    this.$emit("update-list");
                    this.newUserId = response.data.data.id;
                    this.$swal("Done!", "The user was created successfully.", "success");
                })
                .then(() => {
                    mixpanel.track("My Connections - Create a new user", {
                        "New Subscription Quantity": this.subscription.quantity,
                    });
                })
                .catch(e => {
                    this.setErrors(_.get(e, "response.data.errors", {}));
                    helpers.toggleSpinner("create-user-button");
                    console.error(e);
                });
        },

        sendInvite() {
            if (this.newUserId) {
                helpers.toggleSpinner("send-invite-button");
                axios
                    .post(
                        `/api/organizations/${this.organization.id}/users/${this.newUserId}/create`
                    )
                    .then(response => {
                        helpers.toggleSpinner("send-invite-button");
                        this.$swal("Done!", "The invite was sent successfully.", "success");
                        this.resetModal();
                    })
                    .catch(error => {
                        helpers.toggleSpinner("send-invite-button");
                        this.$swal(
                            "Error",
                            "The invite could not be sent. Please contact support if this error persists.",
                            "error"
                        );
                        console.error(error);
                    });
            } else {
                this.$swal(
                    "Error",
                    "The invite could not be sent. Please contact support if this error persists.",
                    "error"
                );
            }
        },
    },
};
</script>

<style scoped></style>
